import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import DocumentViewer from "@/components/DocumentViewer";

/**
 * Documentos auxiliares del proyecto.
 * Vive en el proyecto (NO en la versión): un libro adaptado no cambia entre Plan A/B/C.
 * Se renderiza en la pestaña Guion, debajo del visor del guion principal.
 */

export const DOCUMENT_TYPES = [
  { value: "obra_original", label: "Obra original (adaptación)", icon: "📖" },
  { value: "documentacion_historica", label: "Documentación histórica", icon: "📚" },
  { value: "referencias_visuales", label: "Referencias visuales", icon: "🖼️" },
  { value: "entrevistas", label: "Entrevistas", icon: "🎙️" },
  { value: "tratamiento_anterior", label: "Tratamiento anterior", icon: "📝" },
  { value: "bible_notas", label: "Bible / Notas del director", icon: "🎬" },
  { value: "documentos_legales", label: "Documentos legales", icon: "⚖️" },
  { value: "otros", label: "Otros", icon: "📎" },
];

const typeLabel = (t) => DOCUMENT_TYPES.find((x) => x.value === t)?.label || t;
const typeIcon = (t) => DOCUMENT_TYPES.find((x) => x.value === t)?.icon || "📎";

function fmtBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function ProjectDocuments({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/icaa/projects/${projectId}/documents`);
      setDocs(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id, title) => {
    if (!window.confirm(`¿Eliminar el documento "${title}"?`)) return;
    await api.delete(`/icaa/projects/${projectId}/documents/${id}`);
    load();
  };

  return (
    <section className="mt-8 rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg text-stone-900">Otros documentos del guion</h3>
          <p className="text-[12px] text-stone-500">
            Obra original (si es adaptación), investigación, entrevistas, referencias, contratos…
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="doc-add-btn"
          className="rounded-full bg-[#26775E] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#1c5c48]"
        >
          + Añadir documento
        </button>
      </div>

      {loading && (
        <p className="mt-6 text-center text-sm text-stone-400">Cargando documentos…</p>
      )}

      {!loading && docs.length === 0 && (
        <p className="mt-6 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-[13px] text-stone-500">
          Aún no has añadido ningún documento auxiliar.
        </p>
      )}

      {!loading && docs.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="doc-list">
          {docs.map((d) => (
            <DocumentRow
              key={d.id}
              projectId={projectId}
              doc={d}
              onView={() => setViewing(d)}
              onDelete={() => remove(d.id, d.title)}
            />
          ))}
        </ul>
      )}

      {adding && (
        <AddDocumentModal
          projectId={projectId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}

      {viewing && (
        <DocumentViewer
          projectId={projectId}
          doc={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}

function isPreviewable(doc) {
  if (!doc || doc.source !== "upload") return false;
  const name = (doc.filename || "").toLowerCase();
  const mime = doc.mime || "";
  if (name.endsWith(".pdf") || mime === "application/pdf") return true;
  if (/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(name) || mime.startsWith("image/")) return true;
  if (/\.(txt|fountain|fdx|md|csv|json|xml)$/i.test(name) || mime.startsWith("text/")) return true;
  return false;
}

function DocumentRow({ projectId, doc, onView, onDelete }) {
  const fileUrl = `${API}/icaa/projects/${projectId}/documents/${doc.id}/file`;
  const canPreview = isPreviewable(doc);
  return (
    <li
      data-testid={`doc-row-${doc.id}`}
      className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50/50 p-3 transition hover:border-[#26775E]/40 hover:bg-white"
    >
      <span className="mt-0.5 text-2xl leading-none" aria-hidden>{typeIcon(doc.type)}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#26775E]">
          {typeLabel(doc.type)}
        </p>
        <p className="mt-0.5 font-serif text-[15px] leading-tight text-stone-900">{doc.title}</p>
        {doc.description && (
          <p className="mt-1 text-[12.5px] leading-snug text-stone-600">{doc.description}</p>
        )}
        <p className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-stone-500">
          {doc.source === "upload" ? (
            <>
              <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-medium">Archivo</span>
              <span className="font-mono">{doc.filename}</span>
              {doc.size > 0 && <span>· {fmtBytes(doc.size)}</span>}
            </>
          ) : (
            <>
              <span className="rounded-full bg-sky-200/70 px-2 py-0.5 text-[10px] font-medium text-sky-900">Enlace</span>
              <a href={doc.url} target="_blank" rel="noreferrer"
                 className="truncate font-mono text-[#26775E] hover:underline">
                {doc.url}
              </a>
            </>
          )}
          <span>· {fmtDate(doc.created_at)}</span>
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {doc.source === "upload" ? (
          <>
            {canPreview && (
              <button
                onClick={onView}
                data-testid={`doc-view-${doc.id}`}
                className="rounded-full bg-[#26775E] px-3 py-1 text-[11px] font-semibold text-white hover:bg-[#1c5c48]"
              >
                Ver
              </button>
            )}
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              download={doc.filename}
              data-testid={`doc-download-${doc.id}`}
              className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-700 hover:border-[#26775E] hover:text-[#26775E]"
            >
              Descargar
            </a>
          </>
        ) : (
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`doc-open-${doc.id}`}
            className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-700 hover:border-[#26775E] hover:text-[#26775E]"
          >
            Ir
          </a>
        )}
        <button
          onClick={onDelete}
          data-testid={`doc-delete-${doc.id}`}
          title="Eliminar"
          className="rounded-full border border-stone-300 px-2.5 py-1 text-[11px] text-stone-500 hover:border-red-400 hover:text-red-600"
        >
          ×
        </button>
      </div>
    </li>
  );
}

function AddDocumentModal({ projectId, onClose, onSaved }) {
  const [type, setType] = useState(DOCUMENT_TYPES[0].value);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState("upload");  // "upload" | "link"
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim()) {
      setError("Pon un título descriptivo.");
      return;
    }
    if (mode === "link" && !url.trim()) {
      setError("Pega la URL del documento.");
      return;
    }
    if (mode === "upload" && !file) {
      setError("Selecciona un archivo.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", type);
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    if (mode === "link") fd.append("url", url.trim());
    else fd.append("file", file);
    try {
      await api.post(`/icaa/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        data-testid="doc-add-modal"
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl sm:p-6"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">Otros documentos</p>
            <h3 className="font-display text-xl text-stone-900">Añadir documento</h3>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700">×</button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Tipo</span>
            <select
              value={type} onChange={(e) => setType(e.target.value)}
              data-testid="doc-type"
              className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]"
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.icon}  {t.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Título *</span>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
              data-testid="doc-title"
              placeholder='Ej. "Novela original — Antonio Muñoz Molina, 2014"'
              className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]"
            />
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Descripción (opcional)</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              data-testid="doc-description"
              placeholder="Ej. capítulo 4 de la novela base / cesión firmada por el autor / etc."
              className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]"
            />
          </label>

          <div className="flex gap-1.5 rounded-full bg-stone-100 p-1" data-testid="doc-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("upload")}
              data-testid="doc-mode-upload"
              className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition ${
                mode === "upload" ? "bg-stone-900 text-white" : "text-stone-500"
              }`}
            >
              Subir archivo
            </button>
            <button
              type="button"
              onClick={() => setMode("link")}
              data-testid="doc-mode-link"
              className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition ${
                mode === "link" ? "bg-stone-900 text-white" : "text-stone-500"
              }`}
            >
              Enlace externo
            </button>
          </div>

          {mode === "upload" ? (
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">
                Archivo (PDF, DOCX, EPUB, TXT, JPG, PNG · máx. 25 MB)
              </span>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="doc-file"
                accept=".pdf,.docx,.doc,.epub,.txt,.fountain,.fdx,.jpg,.jpeg,.png,.webp,.heic"
                className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-stone-900 file:px-3 file:py-1 file:text-[11px] file:font-semibold file:uppercase file:tracking-widest file:text-white"
              />
            </label>
          ) : (
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">URL</span>
              <input
                type="url"
                value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://drive.google.com/..., https://dropbox.com/..., etc."
                data-testid="doc-url"
                className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#26775E]"
              />
            </label>
          )}

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-300 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-600 hover:border-stone-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="doc-save"
            className="rounded-full bg-[#26775E] px-5 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-50"
          >
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
